import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { SubmissionError } from 'redux-form';
import numeral from 'numeral';
import { formatValidationErrors } from '../../common/utils/getApiReducer';
import { editLesson, getLessonVideo, uploadVideo } from './actions';

import {
  selectActiveChapter,
  selectActiveCourse,
  selectActiveLesson,
  selectFormattedVideoSections,
  selectVideoSections,
} from '../selectors';

import Breadcrumb from '../../common/components/Breadcrumb';
import dataUrlToFile from '../../common/utils/dataUrlToFile';
import LessonVideoEditForm from './LessonVideoEditForm';
import VideoPlayer from '../../common/video/VideoPlayer';
import QuizBuilder from '../quizzes/QuizBuilder';
import DailyExtraBuilder from '../daily-extras/DailyExtraBuilder';

class LessonEditPage extends Component {
  static propTypes = {
    activeLesson: PropTypes.object,
    lessons: PropTypes.object,
    dispatch: PropTypes.func,
    initialValues: PropTypes.object,
  };

  state = {
    clearOrigImage: false,
    editing: false,
  };

  componentDidMount() {
    const { match: { params: { courseId, chapterId, id } } } = this.props;
    if (this.props.activeLesson.get('video_id') && !this.props.activeLesson.get('video')) {
      this.props.dispatch(getLessonVideo(courseId, chapterId, id)).then(this.handleVideoResponse);
    }
  }

  toggleEdit = () => {
    this.setState(state => ({
      editing: !state.editing,
      clearOrigImage: false,
    }));
  };

  onEditLessonSubmit = values => {
    const { dispatch, match: { params: { courseId, chapterId, id } }, activeLesson } = this.props;
    const { clearOrigImage } = this.state;

    if (values.image) {
      values.image = dataUrlToFile(values.image.imageResult, values.image.imageFilename);
    }

    if (!clearOrigImage) {
      values.image_id = activeLesson.get('image_id');
    }

    return dispatch(editLesson(courseId, chapterId, id, values)).then(action => {
      if (!action.response.ok) {
        throw new SubmissionError(formatValidationErrors(action.json).toJS());
      } else {
        this.setState(state => ({ editing: false }));
      }
    });
  };

  checkVideoStatus = () => {
    const { dispatch, match: { params: { courseId, chapterId, id } } } = this.props;

    dispatch(getLessonVideo(courseId, chapterId, id)).then(this.handleVideoResponse);
  };

  handleVideoResponse = response => {
    if (response.json && response.json.transcode_status !== 'Complete' && response.json.transcode_status !== 'Error') {
      setTimeout(this.checkVideoStatus, 10000);
    }
  };

  onVideoUpload = file => {
    const { dispatch, match: { params: { courseId, chapterId, id } } } = this.props;

    return dispatch(uploadVideo(courseId, chapterId, id, file[0])).then(response => {
      setTimeout(this.checkVideoStatus, 10000);
    });
  };

  removeImage = () => {
    this.setState(state => ({
      clearOrigImage: true,
    }));
  };

  render() {
    const {
      activeCourse,
      activeChapter,
      activeLesson,
      editingQuiz,
      initialValues,
      match,
      lessonVideoUploading,
      lessonVideoUploadProgress,
    } = this.props;
    const { clearOrigImage, editing } = this.state;

    const breadcrumbs = [
      {
        label: 'Courses',
        to: '/courses',
      },
      {
        label: activeCourse.get('name'),
        to: `/courses/${match.params.courseId}`,
      },
      {
        label: activeChapter.get('name'),
        to: `/courses/${match.params.courseId}/chapters/${match.params.chapterId}`,
      },
      {
        label: activeLesson.get('name'),
        to: null,
      },
    ];

    return (
      <div className="admin-paper">
        <div className="admin-paper__header">
          <Breadcrumb crumbs={breadcrumbs} />
        </div>
        {editing ? (
          <LessonVideoEditForm
            clearOrigImage={clearOrigImage}
            lesson={activeLesson}
            initialValues={initialValues}
            onSubmit={this.onEditLessonSubmit}
            onCancel={this.toggleEdit}
            onDelete={this.onDelete}
            onVideoUpload={this.onVideoUpload}
            removeImage={this.removeImage}
            lessonVideoUploading={lessonVideoUploading}
            lessonVideoUploadProgress={lessonVideoUploadProgress}
          />
        ) : (
          <div className="lesson-edit-page">
            <div className="admin-paper__edit-content lesson-video" key="lesson-info">
              <p className="admin-paper__edit-content__title">{activeLesson.get('name')} Lesson Video</p>
              <div className="admin-paper__lesson-row">
                <div className="admin-paper__lesson-row__child">
                  {activeLesson.get('video') ? (
                    <VideoPlayer
                      video={activeLesson.get('video')}
                      key={`${activeLesson.getIn(['video', 'key'])}-${activeLesson.getIn([
                        'video',
                        'transcode_status',
                      ])}`}
                      title={`${activeLesson.get('name')} Lesson Video`}
                      poster={
                        activeLesson.getIn(['image', 'location']) ? (
                          activeLesson.getIn(['image', 'location'])
                        ) : (
                          'https://d27vybgvtr1dba.cloudfront.net/placeholder.jpg'
                        )
                      }
                      sections={activeLesson.get('video_sections').toJS()}
                    />
                  ) : (
                    <div className="admin__image__placeholder image__placeholder">No Video</div>
                  )}
                </div>
                <div className="admin-paper__lesson-row__child">
                  {!activeLesson.get('video_sections').size ? (
                    <div className="admin-paper__lesson-sections image__placeholder">
                      <span>There are currently no video sections</span>
                    </div>
                  ) : (
                    <div className="admin-paper__lesson-sections">
                      <p>Video Sections</p>
                      {activeLesson
                        .get('video_sections')
                        .toArray()
                        .map((section, i) => (
                          <div key={`section-${i}`} className="lesson-sections__row">
                            <span>{section.get('text')}</span>
                            <span>{numeral(section.get('startTime')).format('00:00:00')}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="admin-paper__edit-button">
                <button className="button" disabled={editingQuiz} onClick={this.toggleEdit}>
                  Edit
                </button>
              </div>
            </div>
            <div className="quiz-builder__container">
              <p className="admin-paper__edit-content__title">{activeLesson.get('name')} Quiz</p>
              <QuizBuilder
                lessonId={activeLesson.get('id')}
                quizId={activeLesson.get('quiz_id') ? activeLesson.get('quiz_id') : null}
              />
            </div>
            <div className="daily-extra-builder__container">
              <p className="admin-paper__edit-content__title">{activeLesson.get('name')} Daily Extra</p>
              <DailyExtraBuilder
                lessonId={activeLesson.get('id')}
                dailyExtraId={activeLesson.get('daily_extra_id') ? activeLesson.get('daily_extra_id') : null}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default connect((state, ownProps) => {
  const activeCourse = selectActiveCourse(state.courses.courses, ownProps.match.params.courseId);
  const activeChapter = selectActiveChapter(state.courses.chapters, ownProps.match.params.chapterId);
  const activeLesson = selectActiveLesson(state.courses.lessons, ownProps.match.params.id);

  return {
    activeChapter,
    activeCourse,
    activeLesson,
    editingQuiz: state.courses.editingQuiz,
    lessons: state.courses.lessons,
    initialValues: activeLesson
      ? {
          name: activeLesson.get('name'),
          video_sections: selectFormattedVideoSections(activeLesson),
        }
      : null,
    lessonVideoUploading: state.courses.lessonVideoUploading,
    lessonVideoUploadProgress: state.courses.lessonVideoUploadProgress,
    lessonVideoSections: activeLesson ? selectVideoSections(activeLesson) : {},
  };
})(LessonEditPage);



// WEBPACK FOOTER //
// ./src/courses/lessons/LessonEditPage.js